import { BasePlugin, Events } from 'xgplayer'
import { EVENTS, Context } from 'xgplayer-helper-utils'
import Flv from './flv-live'
import defaultConfig from './config'

const flvAllowedEvents = EVENTS.FlvAllowedEvents

class FlvPlayer extends BasePlugin {
  static get pluginName () {
    return 'flvLive'
  }

  static get defaultConfig () {
    return Object.assign({}, defaultConfig, {
      loadTimeout: 10000,
      maxReaderInterval: 5000,
      seiOnDemand: false,
      preloadTime: 5,
      retryCount: 3,
      retryDelay: 0
    })
  }

  get version () {
    return '__VERSION__'
  }

  constructor (config) {
    super(config)
    this._bindPlayerEvents()
    this._stopStreamWhenNotAutoPlay()
  }

  beforePlayerInit () {
    this._initFlvCtr()
    this.player.switchURL = this._switchURLHandler
    // video.src: the blob url create by mse component
    try {
      BasePlugin.defineGetterOrSetter(this.player, {
        __url: {
          get: () => {
            return this.mse.url
          },
          configurable: true
        }
      })
    } catch (e) {
      // NOOP
    }
  }

  _stopStreamWhenNotAutoPlay () {
    if (!this.playerConfig.url) return

    this._stopFlv = () => {
      if (!this.playerConfig?.autoplay) {
        this._pauseHandler()
      }
    }

    this.once(Events.CANPLAY, this._stopFlv)
  }

  _initFlvCtr () {
    this.context?.destroy()
    const context = new Context(this.player, this.config, flvAllowedEvents)
    const flv = context.registry('FLV_CONTROLLER', Flv)()
    context.init()
    this.flv = flv
    this.context = context
    this.mse = flv.mse
    this._bindFlvEvents(flv)
    this._loadData()
    this.emit('core_inited', flv)
  }

  _bindFlvEvents () {
    const flv = this.flv

    flv.on(EVENTS.REMUX_EVENTS.DETECT_CHANGE_STREAM_DISCONTINUE, (type, dtses) => {
      this.player?.emit(EVENTS.REMUX_EVENTS.DETECT_CHANGE_STREAM_DISCONTINUE, type, dtses)
    })

    flv.on(EVENTS.MSE_EVENTS.MSE_WRONG_TRACK_ADD, this._onMseWrongTrackAddHandler)
  }

  _bindPlayerEvents () {
    this.player?.useHooks('play', this._playHandler)
    this.player?.useHooks('pause', this._pauseHandler)

    if (this._stopFlv) {
      this.off(Events.CANPLAY, this._stopFlv)
      this._stopFlv = null
    }
    this.on(Events.SEEKING, this._seekingHandler)
    this.on(Events.DESTROY, this._destroyHandler)
    this.on(Events.URL_CHANGE, this._switchURLHandler)
    this.on(Events.DEFINITION_CHANGE, this._definitionChangeHandler)
  }

  /** ********* flv controller event handlers *********************** */

  _onMseWrongTrackAddHandler = () => {
    this._reloadStream()
  }
  /** ********* flv event handlers end *********************** */

  /** *********** player event handler *********************** */
  _playHandler = () => {
    if (!this.player || !this.player.video) return Promise.resolve()

    if (this.config.seamlesslyReload) {
      return this._reloadStreamSeamless(this.player.config.url)
    }

    const { video } = this.player
    return Promise.all(
      [
        // for not allowed autoplay by browser
        video.readyState > 1 ? video.pause() : Promise.resolve(),
        this._destroyInternal().then(() => {
          this._bindPlayerEvents()

          // playHook -> videoPlay() -> start()
          this.player.hasStart = false
        })
      ])
  }

  _pauseHandler = () => {
    this.flv?.pause()
  }

  _seekingHandler = () => {
    if (!this.player || !this.player.getBufferedRange) return
    const time = this.player.currentTime
    const range = this.player.getBufferedRange()
    if (time > range[1] || time < range[0]) {
      this.flv && this.flv.seek(this.player.currentTime)
    }
  }

  _definitionChangeHandler = (change) => {
    const { to } = change
    this._switchURLInternal(to)
  }

  _switchURLHandler = (url, seamless) => {
    this._switchURLInternal(url, seamless)
  }

  _destroyHandler = () => {
    return this._destroyInternal()
  }

  /** *********** player event handler end *********************** */

  _loadData (time = this.player.currentTime) {
    if (this.player && this.core) {
      this.core.seek(time)
    }
  }

  // reload stream with mse recreate
  _reloadStream () {
    if (!this.player || !this.player.video) return Promise.resolve()
    this._destroyInternal().then(() => {
      this._bindPlayerEvents()
      this.player.hasStart = false
      this.player.videoPlay()
    })
  }

  /**
   * reload stream without mse recreate
   * @param {string} url the new url to load
   * @returns {Flv} the new controller
   */
  _reloadStreamSeamless (url) {
    if (!this.context) return

    const ended = this.player.ended

    const context = new Context(this.player, this.config, flvAllowedEvents)

    this.tmpContext = context

    const { _dtsBase, _videoDtsBase, _audioDtsBase, _isDtsBaseInited } = this.context.getInstance('MP4_REMUXER').remuxer

    const flv = context.registry('FLV_CONTROLLER', Flv)(this.mse, {
      remux: {
        _dtsBase, _videoDtsBase, _audioDtsBase, _isDtsBaseInited
      }
    })

    context.init()

    this._waitStreamReady(flv, context)
      .then(() => {
        this.flv = flv
        this.mse.resetContext(context, true)
        this.context.destroy()
        this.context = context
        this.tmpContext = null
        this._bindFlvEvents()
      }).catch(() => {
        flv?.pause()
        this.tmpContext = null
      })

    /**
     * HACK!
     *
     * Media append that overlapped current playback position caused time gap in playing VIDEO stream
     * because the next keyframe is 2201ms beyond last overlapped frame. Media may appear temporarily frozen
     *
     * https://github.com/video-dev/hls.js/issues/3596
     */
    flv.onceKeyFrame(() => {
      setTimeout(() => {
        if (!this?.player) return
        this.player.currentTime += 0
      }, 150)
    })

    flv.loadData(url)

    if (ended) {
      this.player.once('progress', () => this.player.video.play().catch(() => {}))
    }
    return flv
  }

  _waitStreamReady (flv) {
    return new Promise((resolve, reject) => {
      let mediaLength = 3
      const _waitReadyHandler = () => {
        mediaLength -= 1
        if (mediaLength === 0 && this.player) {
        // ensure switch smoothly
          this.emit('switch_completed')
          flv.on(EVENTS.DEMUX_EVENTS.ISKEYFRAME, flv._handleKeyFrame)
          flv.off(EVENTS.REMUX_EVENTS.MEDIA_SEGMENT, _waitReadyHandler)
          resolve()
          flv.urlSwitching = true
        }

        if (!this.player) {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject()
        }
      }

      flv.once(EVENTS.LOADER_EVENTS.LOADER_ERROR, () => {
        this.emit('switch_error')
        // eslint-disable-next-line prefer-promise-reject-errors
        reject()
      })

      flv.off(EVENTS.DEMUX_EVENTS.ISKEYFRAME, flv._handleKeyFrame)
      flv.on(EVENTS.REMUX_EVENTS.MEDIA_SEGMENT, _waitReadyHandler)
    })
  }

  /**
   * @param {string} url  the address to play
   * @param {boolean} seamless whether to play with mse recreate
   */
  _switchURLInternal (url, seamless) {
    if (!this.player) return

    this.player.config.url = url

    seamless = seamless && (!!this.context.getInstance('MP4_REMUXER'))

    if (!seamless) {
      this.player.currentTime = 0
      if (this.tmpContext) {
        this.tmpContext.destroy()
        this.tmpContext = null
        this.emit('switch_error')
      }
      this._reloadStream()
      return
    }

    const flv = this._reloadStreamSeamless(url)

    flv && this.emit('core_inited', flv)
  }

  _destroyInternal () {
    if (!this.context) return Promise.resolve()

    if (this.flv && this.context) {
      const loader = this.context.getInstance('FETCH_LOADER')
      loader && loader.destroy()
    }

    const clear = () => {
      if (!this.context) return
      this.context.destroy()
      this.flv = null
      this.context = null
      super.offAll()
    }
    return this.flv && this.flv.mse ? this.flv.mse.destroy().then(clear) : Promise.resolve(clear())
  }

  switchURL (url, seamless) {
    this._switchURLHandler(url, seamless)
  }

  destroy () {
    return this._destroyInternal()
  }

  /** @type {Flv} */
  get core () {
    return this.flv
  }

  get loader () {
    return this._context?.getInstance('FETCH_LOADER')
  }

  get context () {
    return this._context
  }

  set context (ctx) {
    this._context = ctx
  }

  get endedStatus () {
    return this.flv?.endedStatus
  }

  static isSupported () {
    return window.MediaSource &&
      window.MediaSource.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"')
  }
}

export default FlvPlayer
